import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import api from '@/lib/api';

const newRef = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random()}`);

const TITLES = {
  confirm: 'Confirm receipt', deposit: 'Bank deposit by holder', expense: 'Bill paid from held cash',
  return: 'Return cash to branch', pass: 'Pass cash to another person', resolve: 'Resolve open difference', cancel: 'Reverse unconfirmed handover',
};

// One dialog for every custody action; the backend enforces amounts, permissions and duplicates.
export function CustodyActionDialog({ action, record, onClose, onDone, bankAccounts, recipients, branches }) {
  const [f, setF] = useState({});
  const [expenses, setExpenses] = useState(null);
  const [ref] = useState(newRef());
  const [busy, setBusy] = useState(false);
  if (!action || !record) return null;
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  if (action === 'expense' && expenses === null) {
    setExpenses([]);
    api.get('/expenses', { params: { payment_mode: 'cash', limit: 300 } })
      .then(({ data }) => setExpenses((data?.data || data || []).filter((x) => !x.custody_id && !x.funded_by_advance_id)))
      .catch(() => setExpenses([]));
  }

  const send = async () => {
    const amt = f.amount === undefined || f.amount === '' ? undefined : parseFloat(f.amount);
    const id = record.id;
    const calls = {
      confirm: () => api.post(`/cash-custody/${id}/confirm`, { received_amount: amt ?? record.amount, disputed: !!f.disputed, reason: f.reason }),
      deposit: () => api.post(`/cash-custody/${id}/deposit`, { amount: amt, bank_account_id: f.bank_account_id, reference: f.reference, client_ref: ref }),
      expense: () => api.post(`/cash-custody/${id}/link-expense`, { expense_id: f.expense_id, client_ref: ref }),
      return: () => api.post(`/cash-custody/${id}/return`, { amount: amt, to_branch_id: f.to_branch_id || undefined, client_ref: ref }),
      pass: () => api.post('/cash-custody', { source: 'holder', parent_id: id, holder_employee_id: f.holder_employee_id, amount: amt, client_ref: ref }),
      resolve: () => api.post(`/cash-custody/${id}/resolve`, { action: f.resolution || 'received', amount: amt ?? record.in_transit, reason: f.reason }),
      cancel: () => api.post(`/cash-custody/${id}/cancel`, { reason: f.reason }),
    };
    setBusy(true);
    try { await calls[action](); toast.success('Saved'); onDone(); onClose(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Not saved'); }
    finally { setBusy(false); }
  };

  const sel = (k, opts, ph) => (
    <select className="w-full border rounded-md h-9 px-2 text-sm bg-background" value={f[k] || ''} onChange={set(k)} data-testid={`custody-${action}-${k}`}>
      <option value="">{ph}</option>
      {opts.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
    </select>
  );

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid={`custody-dialog-${action}`}>
        <DialogHeader><DialogTitle className="font-outfit">{TITLES[action]}</DialogTitle></DialogHeader>
        <div className="text-xs text-muted-foreground">{record.holder_name} · owned by {record.from_branch_name} · holds {Number(record.held_balance || 0).toFixed(2)} · open {Number(record.in_transit || 0).toFixed(2)}</div>
        <div className="space-y-3">
          {action !== 'expense' && action !== 'cancel' && (
            <div><Label>Amount</Label><Input type="number" step="0.01" value={f.amount ?? ''} onChange={set('amount')}
              placeholder={action === 'confirm' ? String(record.amount) : action === 'resolve' ? String(record.in_transit) : ''} data-testid={`custody-${action}-amount`} /></div>
          )}
          {action === 'deposit' && sel('bank_account_id', bankAccounts.map((b) => ({ id: b.id, label: `${b.bank_name || b.name} · ${(b.account_number || '').slice(-4)}` })), 'Bank account…')}
          {action === 'deposit' && <Input placeholder="Bank slip / reference" value={f.reference || ''} onChange={set('reference')} data-testid="custody-deposit-reference" />}
          {action === 'expense' && sel('expense_id', (expenses || []).map((x) => ({ id: x.id, label: `${String(x.date || '').slice(0, 10)} · ${x.category || ''} · ${x.description || ''} · ${Number(x.amount).toFixed(2)}` })), 'Recorded cash bill…')}
          {action === 'return' && sel('to_branch_id', branches.map((b) => ({ id: b.id, label: b.name })), `Back to ${record.from_branch_name}`)}
          {action === 'pass' && sel('holder_employee_id', recipients.map((p) => ({ id: p.id, label: p.name })), 'New holder…')}
          {action === 'resolve' && sel('resolution', [{ id: 'received', label: 'Found — the holder has it' }, { id: 'returned', label: 'Returned to branch cash' }], 'Outcome…')}
          {action === 'confirm' && (
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!f.disputed} onChange={(e) => setF({ ...f, disputed: e.target.checked })} data-testid="custody-confirm-disputed" /> Amount is wrong — dispute the difference</label>
          )}
          {(action === 'confirm' || action === 'resolve' || action === 'cancel') && (
            <Input placeholder={action === 'confirm' ? 'Reason (required when confirming for someone else)' : 'Reason (required)'} value={f.reason || ''} onChange={set('reason')} data-testid={`custody-${action}-reason`} />
          )}
          <Button className="rounded-full" disabled={busy} onClick={send} data-testid={`custody-${action}-submit`}>Save</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
